import {z} from 'zod'

const parseOptionalSampleRate = (val) => {
	if (val === undefined) return undefined

	if (typeof val === 'string') {
		const trimmedValue = val.trim()

		if (trimmedValue === '') return undefined

		const parsedValue = Number(trimmedValue)

		return Number.isFinite(parsedValue) &&
			parsedValue >= 0 &&
			parsedValue <= 1
			? parsedValue
			: undefined
	}

	const parsedValue = typeof val === 'number' ? val : Number.NaN

	return Number.isFinite(parsedValue) && parsedValue >= 0 && parsedValue <= 1
		? parsedValue
		: undefined
}

const parseOptionalBoolean = (val) => {
	if (val === undefined) return undefined
	if (typeof val === 'boolean') return val

	if (typeof val === 'string') {
		const trimmedValue = val.trim().toLowerCase()

		if (trimmedValue === '') return undefined
		if (trimmedValue === 'true') return true
		if (trimmedValue === 'false') return false
	}

	return undefined
}

const parseOptionalUrl = (val) => {
	if (val === undefined) return undefined

	if (typeof val === 'string') {
		const trimmedValue = val.trim()

		// "none" is the Key Vault sentinel for "no CDN configured" (see next.config.js)
		if (trimmedValue === '' || trimmedValue.toLowerCase() === 'none') {
			return undefined
		}

		return trimmedValue
	}

	return val
}

/**
 * Specify your server-side environment variables schema here.
 * This way you can ensure the app isn't built with invalid env vars.
 */
export const serverSchema = z.object({
	// Application Insights
	ENABLE_CONSOLE_LOG: z.preprocess((val) => {
		if (typeof val === 'boolean') return val
		if (typeof val === 'string')
			return ['true', 'false'].includes(val) ? val === 'true' : false
		return false
	}, z.boolean()),
	// Kontent.ai - get these from the Kontent project you want to pull from
	KONTENT_PREVIEW_API_KEY: z.string(),
	KONTENT_PREVIEW_SECRET: z.string(),
	GLOBAL_CONFIG_NAME: z.string().default('global_config'),
	// Get these from the Pet Names Finder (COM-NAME)
	PET_NAMES_URL: z.string().url().optional(),
	PET_NAMES_API_KEY: z.string().optional(),
	// Get these from CDC
	CDC_DATACENTER: z.string(),
	// Used by E2E teardown and the orphan-cleanup runner to target the CDC site.
	// Browser Screen-Sets still take their API key from Kontent Global Config.
	CDC_API_KEY: z.string().optional(),
	CDC_API_USER_KEY: z.string(),
	CDC_API_SECRET: z.string(),
	// Dedicated non-production CDC API user for E2E teardown and orphan cleanup.
	// Needs accounts.deleteAccount (Playwright teardown) and accounts.search
	// (scheduled sweep). Optional so environments that have not been given one
	// yet fall back to CDC_API_USER_KEY/CDC_API_SECRET, and so production never
	// needs it at all. See docs/testing/cdc-test-strategy.md.
	CDC_E2E_API_USER_KEY: z.string().optional(),
	CDC_E2E_API_SECRET: z.string().optional(),
	// Get these from the Disease API
	DISEASE_API: z.string().url(),
	DISEASE_API_SUBSCRIPTIONKEY: z.string(),
	DISEASE_API_TOKEN_URL: z.string().url(),
	DISEASE_API_CLIENTID: z.string(),
	DISEASE_API_CLIENTSECRET: z.string(),
	// Get these from Business Finder
	FIND_A_BUSINESSMAP_URL: z.string().url(),
	FIND_A_BUSINESS_API_KEY: z.string(),
	BUSINESS_FUZZY_SEARCH_URL: z.string().optional(),
	FIND_A_BUSINESS_API_CLIENT_ID: z.string(),
	FIND_A_BUSINESS_API_CLIENT_SECRET: z.string(),
	FIND_A_BUSINESS_API_TOKEN_URL: z.string().url(),
	// Get these from SendGrid
	SENDGRID_API_KEY: z.string(),
	// Get this from Google
	GOOGLE_GEOCODE_KEY: z.string(),
	// Consumer Engagement API
	PET_ENDPOINT: z.string(),
	// Veeva
	VEEVA_DOMAIN: z.string(),
	VEEVA_VER: z.string(),
	VEEVA_USER: z.string().optional(),
	VEEVA_PASS: z.string().optional(),
	// Campaign Interest Service
	TENANT_ID: z.string(),
	/**
	 * Service Principal Credentials To Make
	 * Authenticated Calls To Other Elanco Applications
	 * TODO: Revisit Bringing In Client Id/Secret
	 * Of Other Applications (ex. DISEASE_API*)
	 */

	LAKEHOUSE_API: z.string().url(),
	LAKEHOUSE_API_SUBSCRIPTIONKEY: z.string(),
	CAMPAIGN_INTEREST_DATABASE: z.string(),
	CAMPAIGN_INTEREST_CONTAINER: z.string(),
	CAMPAIGN_INTEREST_ENDPOINT: z.string(),
	CAMPAIGN_INTEREST_KEY: z.string(),

	/**
	 * Web Activity Data Product API (used in Campaign Interest)
	 */
	WEB_ACTIVITY_DATA_PRODUCT_API_URL: z.string().url(),
	WEB_ACTIVITY_DATA_PRODUCT_API_CLIENT_ID: z.string().uuid(),
	WEB_ACTIVITY_DATA_PRODUCT_API_CLIENT_SECRET: z.string(),
	WEB_ACTIVITY_DATA_PRODUCT_API_TOKEN_URL: z.string().url(),

	IDGW_API_SCOPE: z.string(),
	IDGW_AZURE_CLIENT: z.string(),
	IDGW_AZURE_SECRET: z.string(),
	IDGW_AZURE_TENANT: z.string(),
	IDGW_CAMPAIGN_URL: z.string().url(),
	TRUSTCODE_REPO_KEY: z.string().optional(),
	APP_ENVIRONMENT: z.string(),
})

/**
 * Specify your client-side environment variables schema here.
 * This way you can ensure the app isn't built with invalid env vars.
 * To expose them to the client, prefix them with `NEXT_PUBLIC_`.
 */
export const clientSchema = z.object({
	NEXT_PUBLIC_KONTENT_PROJECT_ID: z.string(),
	NEXT_PUBLIC_APPINSIGHTS_CONNECTION_STRING: z.string(),
	NEXT_PUBLIC_TOWER: z.enum(['pet', 'vet', 'farm', 'corporate']),
	NEXT_PUBLIC_SEARCH_API_KEY: z.string(),
	NEXT_PUBLIC_MAP_API_KEY: z.string(),
	NEXT_PUBLIC_PARASITE_MAP_STYLE_ID: z.string(),
	NEXT_PUBLIC_FAQ_COGNITIVE_SEARCH_API_KEY: z.string(),
	NEXT_PUBLIC_APP_URL: z.string(),
	NEXT_PUBLIC_REBATES_API_URL: z.string().optional(),
	NEXT_PUBLIC_TRUSTCODE_HOST: z.string().optional(),
	NEXT_PUBLIC_CDN_URL: z.preprocess(
		parseOptionalUrl,
		z.string().url().optional()
	),
	NEXT_PUBLIC_FEATURE_FLAG_COUNTRY_SELECTOR: z.string(),
	NEXT_PUBLIC_CWV_APP_INSIGHTS_SAMPLE_RATE: z.preprocess(
		parseOptionalSampleRate,
		z.number().min(0).max(1).optional()
	),
	NEXT_PUBLIC_CWV_GTM_SAMPLE_RATE: z.preprocess(
		parseOptionalSampleRate,
		z.number().min(0).max(1).optional()
	),
	NEXT_PUBLIC_CWV_DEBUG_LOGGING: z.preprocess(
		parseOptionalBoolean,
		z.boolean().optional()
	),
	NEXT_PUBLIC_NODE_ENV: z.enum(['development', 'production', 'test']),
	NEXT_PUBLIC_EMAIL_FOR_PET_LOGIN: z.string().optional(),
	NEXT_PUBLIC_PASS_FOR_PET_LOGIN: z.string().optional(),
	NEXT_PUBLIC_EMAIL_FOR_VET_LOGIN: z.string().optional(),
	NEXT_PUBLIC_PASS_FOR_VET_LOGIN: z.string().optional(),
})
